import { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { supabase } from "../lib/supabase";
import type { Item } from "../lib/types";
import { Notice, PageHeading } from "../components/Ui";

type QrItem = Item & { qr: string };
type Message = { tone: "success" | "error"; text: string } | null;

export function QrPage() {
  const [items, setItems] = useState<QrItem[]>([]);
  const [location, setLocation] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState<Message>(null);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("selection") !== "import") return;
    try {
      const stored = JSON.parse(sessionStorage.getItem("stock-qr-import-selection") ?? "[]");
      if (Array.isArray(stored)) setSelectedIds(stored.filter((value) => Number.isInteger(value)));
    } catch {
      sessionStorage.removeItem("stock-qr-import-selection");
    }
  }, []);

  useEffect(() => {
    supabase.from("items").select("*").eq("active", true).order("brand").then(async ({ data }) => {
      const source = (data ?? []) as Item[];
      const withQr = await Promise.all(source.map(async (item) => ({
        ...item,
        qr: await QRCode.toDataURL(`${window.location.origin}/item/${item.id}`, {
          margin: 1,
          color: { dark: "#0b1f33", light: "#ffffff" },
          width: 240,
        }),
      })));
      setItems(withQr);
    });
  }, []);

  const locations = useMemo(() => [...new Set(items.map((item) => item.location))].sort(), [items]);
  const selectedItems = selectedIds.length > 0 ? items.filter((item) => selectedIds.includes(item.id)) : items;
  const visible = location ? selectedItems.filter((item) => item.location === location) : selectedItems;

  const clearSelection = () => {
    sessionStorage.removeItem("stock-qr-import-selection");
    setSelectedIds([]);
    window.history.replaceState({}, "", "/qr");
  };

  const exportQrDatabase = async () => {
    if (items.length === 0) return setMessage({ tone: "error", text: "Aucun QR code à exporter." });
    setExporting(true);
    setMessage(null);
    try {
      const excelModule = await import("exceljs") as typeof import("exceljs") & { default?: typeof import("exceljs") };
      const WorkbookConstructor = excelModule.Workbook ?? excelModule.default?.Workbook;
      if (!WorkbookConstructor) throw new Error("Le moteur Excel n’a pas pu être chargé par le navigateur.");

      const workbook = new WorkbookConstructor();
      workbook.creator = "Stock Conférence STC";
      workbook.created = new Date();
      workbook.modified = new Date();

      const sheet = workbook.addWorksheet("Base QR", {
        views: [{ state: "frozen", ySplit: 4 }],
        pageSetup: { orientation: "landscape", fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
      });
      sheet.mergeCells("A1:G1");
      sheet.getCell("A1").value = "Base de données QR — Stock Conférence STC";
      sheet.getCell("A1").font = { name: "Arial", size: 18, bold: true, color: { argb: "FFFFFFFF" } };
      sheet.getCell("A1").alignment = { vertical: "middle", horizontal: "left" };
      sheet.getCell("A1").fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0B1F33" } };
      sheet.getRow(1).height = 34;

      sheet.mergeCells("A2:G2");
      sheet.getCell("A2").value = `Export du ${new Intl.DateTimeFormat("fr-BE", { dateStyle: "long", timeStyle: "short" }).format(new Date())} · ${items.length} QR codes`;
      sheet.getCell("A2").font = { name: "Arial", size: 10, color: { argb: "FF66717D" } };
      sheet.getCell("A2").alignment = { vertical: "middle", horizontal: "left" };
      sheet.getRow(2).height = 23;

      const headers = ["Référence", "Marque", "Machine / modèle", "Bloc / stock", "Étagère", "Lien vers la fiche", "QR code"];
      const headerRow = sheet.getRow(4);
      headerRow.values = headers;
      headerRow.height = 25;
      headerRow.eachCell((cell) => {
        cell.font = { name: "Arial", size: 10, bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF176B9C" } };
        cell.alignment = { vertical: "middle", horizontal: "left" };
        cell.border = { bottom: { style: "thin", color: { argb: "FF0B1F33" } } };
      });

      sheet.columns = [
        { key: "reference", width: 12 },
        { key: "brand", width: 22 },
        { key: "model", width: 34 },
        { key: "stock", width: 17 },
        { key: "location", width: 14 },
        { key: "link", width: 48 },
        { key: "qr", width: 16 },
      ];

      items.forEach((item, index) => {
        const rowNumber = index + 5;
        const row = sheet.getRow(rowNumber);
        const itemUrl = `${window.location.origin}/item/${item.id}`;
        row.values = [
          String(item.id).padStart(3, "0"),
          item.brand,
          item.model,
          item.stock_area,
          item.location,
          { text: itemUrl, hyperlink: itemUrl, tooltip: itemUrl },
          "",
        ];
        row.height = 76;
        row.eachCell((cell, columnNumber) => {
          cell.font = { name: "Arial", size: columnNumber === 1 ? 11 : 10, bold: columnNumber === 2 || columnNumber === 5 };
          cell.alignment = { vertical: "middle", horizontal: columnNumber === 1 || columnNumber === 5 ? "center" : "left", wrapText: true };
          cell.border = {
            top: { style: "thin", color: { argb: "FFDDE5EA" } },
            bottom: { style: "thin", color: { argb: "FFDDE5EA" } },
          };
          if (index % 2 === 1) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF4F7F9" } };
        });
        sheet.getCell(rowNumber, 6).font = { name: "Arial", size: 10, color: { argb: "FF176B9C" }, underline: true };

        const imageId = workbook.addImage({ base64: item.qr, extension: "png" });
        sheet.addImage(imageId, { tl: { col: 6.18, row: rowNumber - 0.93 }, ext: { width: 68, height: 68 } });
      });

      sheet.autoFilter = { from: "A4", to: `G${items.length + 4}` };
      sheet.getColumn(1).numFmt = "@";
      sheet.headerFooter.oddFooter = "&LStock Conférence STC&CPage &P / &N&RBase QR";

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `base-qr-stock-stc-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setMessage({ tone: "success", text: `Le fichier Excel avec ${items.length} QR codes a été téléchargé.` });
    } catch (error) {
      const detail = error instanceof Error && error.message ? ` Détail : ${error.message}` : "";
      setMessage({ tone: "error", text: `L’export de la base QR n’a pas pu être créé.${detail}` });
    } finally {
      setExporting(false);
    }
  };

  return <>
    <div className="no-print">
      <PageHeading
        eyebrow="Étiquettes d’armoire"
        title="QR codes"
        description={selectedIds.length > 0 ? `${selectedIds.length} étiquette(s) créée(s) ou modifiée(s) lors du dernier import.` : "Le scan ouvre directement l’article après connexion."}
        action={<div className="heading-actions">
          <button className="secondary-button" type="button" onClick={exportQrDatabase} disabled={exporting || items.length === 0}>{exporting ? "Création du fichier…" : "Exporter la base QR"}</button>
          <button className="primary-button" type="button" onClick={() => window.print()}>Imprimer les étiquettes</button>
        </div>}
      />
      {message && <Notice tone={message.tone}>{message.text}</Notice>}
      {selectedIds.length > 0 && <div className="qr-selection-notice"><strong>Sélection du dernier import</strong><span>Les QR restent les mêmes pour les anciennes références, mais les étiquettes affichent maintenant le nouvel emplacement.</span><button className="text-button" type="button" onClick={clearSelection}>Afficher tous les QR</button></div>}
      <div className="filter-bar compact"><label><span>Emplacement</span><select value={location} onChange={(event) => setLocation(event.target.value)}><option value="">Tous les emplacements</option>{locations.map((value) => <option key={value}>{value}</option>)}</select></label></div>
    </div>
    <section className="qr-grid">{visible.map((item) => <article className="qr-label" key={item.id}><img src={item.qr} alt={`QR code de ${item.brand} ${item.model}`} /><div className="qr-copy"><strong>{item.brand}</strong><span>{item.model}</span><b className="qr-location">{item.location}</b></div></article>)}</section>
  </>;
}
